import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { AxiosInstance } from 'axios'

import { EapToHpiBannerStatus } from '@lyrahealth-inc/shared-app-logic/src/features/eligibility/types'

import { fetchEapHpiBannerState, setEapHpiBannerStateIsLoaded } from './eapHpiBannerActions'
import { getEapHpiBannerStatus, getIsEapHpiBannerLoaded } from './eapHpiBannerSelectors'
import { getIsEapHpiInProductNotificationsEnabled } from '../featureFlags/featureFlagSelectors'
import { useAppDispatch } from '../storeConfiguration/store'
import {
  getEapHpiAccountBannerNotificationState,
  getEapHpiHomebaseBannerNotificationState,
  getId,
} from '../user/userSelectors'

interface UseEapHpiBannerDataProps {
  axiosInstance: AxiosInstance
  notificationType: 'homebase' | 'account'
}

/**
 * Redux-aware hook for accessing EAP to HPI banner data
 * Automatically fetches banner data when conditions are met
 */
export const useEapHpiBannerData = ({
  axiosInstance,
  notificationType,
}: UseEapHpiBannerDataProps): EapToHpiBannerStatus => {
  const dispatch = useAppDispatch()

  // Get data from Redux store
  const isFeatureEnabled = useSelector(getIsEapHpiInProductNotificationsEnabled)
  const notificationState = useSelector(
    notificationType === 'homebase'
      ? getEapHpiHomebaseBannerNotificationState
      : getEapHpiAccountBannerNotificationState,
  )
  const userLyraId = useSelector(getId)
  const bannerStatus = useSelector(getEapHpiBannerStatus)
  const isEapHpiBannerStateLoaded = useSelector(getIsEapHpiBannerLoaded)

  // Fetch banner data when conditions are met
  useEffect(() => {
    const isEnabled = isFeatureEnabled && userLyraId
    const shouldFetch =
      isEnabled && (notificationState === 'new' || notificationState === 'seen') && !isEapHpiBannerStateLoaded

    if (shouldFetch) {
      dispatch(fetchEapHpiBannerState({ userLyraId, axiosInstance }))
    } else {
      dispatch(setEapHpiBannerStateIsLoaded(true))
    }
  }, [
    isFeatureEnabled,
    userLyraId,
    notificationState,
    isEapHpiBannerStateLoaded,
    notificationType,
    dispatch,
    axiosInstance,
  ])

  return bannerStatus?.toJS() as EapToHpiBannerStatus
}
